import React, { Component } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input } from "reactstrap";
import {
  getTransportRequests,
  acceptTransportRequest,
  rejectTransportRequest,
  cancelTransportRequest,
} from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import { canWrite } from "../../services/PermissionService";
import {
  C, Panel, MiniStat, StatRow, SearchPill, TabPills, Table, Chip, Btn, IconBtn, MODAL_CSS,
} from "../../components/Console/ConsoleUI";
import PeriodFilter, { allTime, periodToParams } from "../../components/Common/PeriodFilter";
import ConfirmModal from "../../components/Common/ConfirmModal";

/**
 * Transport requests — console screen D1 ("Web Transport Requests").
 *
 * A reskin. The same /transportRequest list call with the same `status`
 * parameter behind the tabs, and the same accept / reject / cancel endpoints
 * behind the row controls. What changed is the shape: KPI tiles over a quiet
 * table, a trip told as origin → destination rather than a row of coloured
 * badges, and the driver given a column of their own so an unassigned ride is
 * visible at a glance — the one thing this screen exists to catch.
 *
 * The design's "New ride" button is not built: there is no admin-side endpoint
 * that creates a transport request (guests raise them from the app), and
 * inventing one is out of scope for a reskin.
 */

const CSS = MODAL_CSS;

const CATEGORY_LABELS = {
  protocol: "Protocol",
  transport: "Transport",
  protocol_transport: "Protocol & transport",
  day_rental: "Day rental",
  outstation: "Outstation",
  airport: "Airport transfer",
  intercity: "Inter-city",
};

const SERVICE_TYPE_LABELS = {
  arrival: "Arrival",
  departure: "Departure",
  both: "Arrival & departure",
};

const TIER_LABELS = { standard: "Standard", comfort: "Comfort", vip: "VIP" };

const STATUS_LABELS = {
  pending: "Pending",
  accepted: "Accepted",
  rejected: "Rejected",
  driver_assigned: "Driver assigned",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

/** Stage colour follows meaning: waiting on us is amber, moving is green. */
const STATUS_TONE = {
  pending: "warn",
  accepted: "good",
  driver_assigned: "good",
  in_progress: "good",
  completed: "neutral",
  rejected: "bad",
  cancelled: "neutral",
};

const QUOTE_LABELS = {
  pending_quote: "Needs quote",
  quoted: "Awaiting guest",
  accepted: "Quote accepted",
  declined: "Quote declined",
};

const QUOTE_TONE = {
  pending_quote: "warn",
  quoted: "neutral",
  accepted: "good",
  declined: "bad",
};

/** Service categories that put a driver behind the wheel. */
const NEEDS_DRIVER = [
  "transport", "protocol_transport", "day_rental", "outstation", "airport", "intercity",
];

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const shortDate = (d) => {
  if (!d) return null;
  const parsed = new Date(d);
  if (isNaN(parsed)) return null;
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/** Two-line table cell — the design's repeated "value over caption" shape. */
const Stack = ({ top, sub, tone }) => (
  <div>
    <div style={{ fontSize: 14, color: tone || C.ink }}>{top}</div>
    {sub ? <div style={{ marginTop: 2, fontSize: 12, color: C.taupeLight }}>{sub}</div> : null}
  </div>
);

class TransportRequestList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      isLoaded: false,
      statusFilter: "",
      period: allTime(),
      activeTab: "all",
      // View-only filter. It never changes what is fetched.
      search: "",
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Transport Requests", link: "#" },
      ],
      // Modal states
      showAcceptModal: false,
      showReasonModal: false,
      reasonModalType: "", // "reject" or "cancel"
      selectedRequestId: null,
      reasonText: "",
      actionLoading: false,
    };
  }

  handlePeriodChange = (period) => {
    this.setState({ period }, this.getAllRequests);
  };

  componentDidMount() {
    this.getAllRequests();
  }

  getAllRequests = () => {
    const { statusFilter } = this.state;
    const params = {};
    if (statusFilter) params.status = statusFilter;
    Object.assign(params, periodToParams(this.state.period));

    getTransportRequests(params)
      .then((result) => {
        if (result && result.status === 200) {
          // API returns { data: { requests: [...], pagination: {...} } }
          const requests = result.data?.requests || result.data || [];
          this.setState({ isLoaded: true, items: requests });
        } else {
          this.setState({ isLoaded: true });
          toast.error(result?.message || "Failed to fetch requests");
        }
      })
      .catch((err) => {
        console.error("Error fetching transport requests:", err);
        this.setState({ isLoaded: true });
        toast.error("Error fetching transport requests");
      });
  };

  // --- Row derivations -------------------------------------------------------

  /** When this ride happens, from whichever detail block the category uses. */
  when = (r) => {
    const cat = r.serviceCategory || "airport";
    if (cat === "day_rental") {
      return {
        date: r.dayRentalDetails?.date || r.pickupDetails?.date,
        time: r.dayRentalDetails?.startTime || r.dayRentalDetails?.time || r.pickupDetails?.time,
      };
    }
    if (cat === "outstation") {
      return {
        date: r.outstationDetails?.departureDate || r.outstationDetails?.date || r.pickupDetails?.date,
        time: r.outstationDetails?.departureTime || r.outstationDetails?.time || r.pickupDetails?.time,
      };
    }
    return {
      date: r.pickupDetails?.date || r.dropDetails?.date,
      time: r.pickupDetails?.time || r.dropDetails?.time,
    };
  };

  /** The trip, told as origin → destination when the record carries both. */
  trip = (r) => {
    const cat = r.serviceCategory || "airport";
    const bits = [];
    if (cat === "outstation") {
      const from = r.outstationDetails?.pickupLocation;
      const to = r.outstationDetails?.dropLocation || r.outstationDetails?.visitingPlace;
      if (from || to) bits.push([from, to].filter(Boolean).join(" → "));
    } else if (cat === "day_rental") {
      if (r.dayRentalDetails?.address) bits.push(r.dayRentalDetails.address);
    } else {
      const from = r.pickupDetails?.location || r.dropDetails?.pickupLocation;
      const to = r.pickupDetails?.dropoffLocation || r.dropDetails?.location;
      if (from || to) bits.push([from, to].filter(Boolean).join(" → "));
    }
    const headline = bits[0] || CATEGORY_LABELS[cat] || cat;

    const caption = [
      bits.length ? CATEGORY_LABELS[cat] : null,
      SERVICE_TYPE_LABELS[r.serviceType],
      TIER_LABELS[r.tierType],
      cat === "day_rental" && r.rentalHours ? `${r.rentalHours} h` : null,
      cat === "outstation" && r.tripType
        ? r.tripType === "one_way" ? "One-way" : "Return"
        : null,
      `${r.passengers || 1} pax`,
    ]
      .filter(Boolean)
      .join(" · ");

    return { headline, caption };
  };

  /** Fare, honouring the quote workflow day-rental and outstation rides use. */
  fare = (r) => {
    if (r.quoteStatus === "pending_quote") return { text: "Quote pending", muted: true };
    if (r.quoteStatus === "quoted") return { text: money(r.quotedPrice?.amount), muted: false };
    return { text: money(r.pricing?.totalPrice), muted: false };
  };

  /** Client-side text filter over what the current tab already fetched. */
  visibleRows = () => {
    const q = this.state.search.trim().toLowerCase();
    if (!q) return this.state.items;
    return this.state.items.filter((r) => {
      const t = this.trip(r);
      return [
        r.requestNumber,
        r.contactName,
        r.userId?.name,
        r.contactMobile,
        r.assignedDriver?.name,
        r.assignedDriver?.vehicleNumber,
        t.headline,
        STATUS_LABELS[r.status],
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  };

  columns = () => [
    {
      key: "requestNumber",
      label: "Request",
      width: 120,
      render: (r) => {
        const w = this.when(r);
        return (
          <Stack
            top={<span style={{ fontWeight: 500 }}>{r.requestNumber}</span>}
            sub={[shortDate(w.date), w.time].filter(Boolean).join(" · ") || null}
          />
        );
      },
    },
    {
      key: "guest",
      label: "Guest",
      render: (r) => (
        <Stack top={r.contactName || r.userId?.name || "—"} sub={r.contactMobile} />
      ),
    },
    {
      key: "trip",
      label: "Trip",
      render: (r) => {
        const t = this.trip(r);
        return <Stack top={t.headline} sub={t.caption} />;
      },
    },
    {
      key: "driver",
      label: "Driver",
      render: (r) => {
        if (r.assignedDriver?.name) {
          return (
            <Stack
              top={r.assignedDriver.name}
              sub={r.assignedDriver.vehicleNumber || r.assignedDriver.mobile}
            />
          );
        }
        // Red is for a gap someone still has to close. A protocol-only booking
        // never needs a driver, and a finished or cancelled ride no longer
        // does — flagging those trains people to ignore the colour.
        const open = ["pending", "accepted"].includes(r.status);
        const needsOne = NEEDS_DRIVER.includes(r.serviceCategory || "airport");
        return open && needsOne ? (
          <Stack top="Unassigned" sub="needs a driver" tone={C.red} />
        ) : (
          <Stack top={<span style={{ color: C.taupeLight }}>—</span>} />
        );
      },
    },
    {
      key: "fare",
      label: "Fare",
      width: 120,
      render: (r) => {
        const f = this.fare(r);
        return (
          <span style={{ fontSize: 15, fontWeight: 500, color: f.muted ? C.taupeLight : C.ink }}>
            {f.text}
          </span>
        );
      },
    },
    {
      key: "status",
      label: "Stage",
      width: 170,
      render: (r) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          <Chip tone={STATUS_TONE[r.status] || "neutral"}>
            {STATUS_LABELS[r.status] || r.status}
          </Chip>
          {r.quoteStatus && r.quoteStatus !== "not_applicable" && (
            <Chip tone={QUOTE_TONE[r.quoteStatus] || "neutral"}>
              {QUOTE_LABELS[r.quoteStatus] || r.quoteStatus}
            </Chip>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      label: "",
      align: "right",
      width: 150,
      render: (r) => (
        <div
          style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}
          onClick={(e) => e.stopPropagation()}
        >
          {r.status === "pending" && (
            <React.Fragment>
              <IconBtn icon="check" title="Accept" onClick={() => this.handleAccept(r._id)} />
              {canWrite() && (
                <IconBtn icon="close" title="Reject" tone="bad" onClick={() => this.handleReject(r._id)} />
              )}
            </React.Fragment>
          )}
          {["pending", "accepted", "driver_assigned"].includes(r.status) && (
            <IconBtn icon="block" title="Cancel" tone="warn" onClick={() => this.handleCancel(r._id)} />
          )}
          <IconBtn
            icon="chevron_right"
            title="Open request"
            onClick={() => this.props.history.push(`/transport/request/${r._id}`)}
          />
        </div>
      ),
    },
  ];

  // --- Actions (unchanged) ---------------------------------------------------

  toggleAcceptModal = () => this.setState({ showAcceptModal: !this.state.showAcceptModal });
  toggleReasonModal = () => this.setState({ showReasonModal: !this.state.showReasonModal, reasonText: "" });

  handleAccept = (id) => {
    this.setState({ selectedRequestId: id, showAcceptModal: true });
  };

  confirmAccept = () => {
    const { selectedRequestId } = this.state;
    this.setState({ actionLoading: true });
    acceptTransportRequest(selectedRequestId)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Request accepted successfully");
          this.getAllRequests();
        } else {
          toast.error(response?.message || "Failed to accept request");
        }
      })
      .catch(() => toast.error("Error accepting request"))
      .finally(() => {
        this.setState({ actionLoading: false, showAcceptModal: false, selectedRequestId: null });
      });
  };

  handleReject = (id) => {
    this.setState({ selectedRequestId: id, reasonModalType: "reject", showReasonModal: true });
  };

  handleCancel = (id) => {
    this.setState({ selectedRequestId: id, reasonModalType: "cancel", showReasonModal: true });
  };

  confirmReasonAction = () => {
    const { selectedRequestId, reasonModalType, reasonText } = this.state;
    if (!reasonText.trim()) {
      toast.error("Please enter a reason");
      return;
    }
    this.setState({ actionLoading: true });

    const apiCall = reasonModalType === "reject"
      ? rejectTransportRequest(selectedRequestId, reasonText)
      : cancelTransportRequest(selectedRequestId, reasonText);

    apiCall
      .then((response) => {
        if (response && response.status === 200) {
          toast.success(reasonModalType === "reject" ? "Request rejected" : "Request cancelled");
          this.getAllRequests();
        } else {
          toast.error(response?.message || `Failed to ${reasonModalType} request`);
        }
      })
      .catch(() => toast.error(`Error ${reasonModalType}ing request`))
      .finally(() => {
        this.setState({
          actionLoading: false,
          showReasonModal: false,
          selectedRequestId: null,
          reasonText: "",
          reasonModalType: "",
        });
      });
  };

  setActiveTab = (tab) => {
    const statusMap = {
      all: "",
      pending: "pending",
      accepted: "accepted",
      assigned: "driver_assigned",
      completed: "completed",
      cancelled: "cancelled",
    };

    this.setState(
      { activeTab: tab, statusFilter: statusMap[tab] || "" },
      () => this.getAllRequests()
    );
  };

  render() {
    const { items, isLoaded, activeTab, search } = this.state;
    const rows = this.visibleRows();

    // Counted over what this tab fetched, and labelled as such — a figure that
    // silently changes meaning when a tab is clicked is worse than no figure.
    const awaiting = items.filter((r) => r.status === "pending").length;
    const moving = items.filter((r) =>
      ["accepted", "driver_assigned", "in_progress"].includes(r.status)
    ).length;
    const unassigned = items.filter(
      (r) =>
        !r.assignedDriver?.name &&
        ["accepted", "pending"].includes(r.status) &&
        NEEDS_DRIVER.includes(r.serviceCategory || "airport")
    ).length;
    const value = items.reduce(
      (s, r) =>
        s +
        Number(
          r.quoteStatus === "quoted" ? r.quotedPrice?.amount || 0 : r.pricing?.totalPrice || 0
        ),
      0
    );

    const tabs = [
      { value: "all", label: "All" },
      { value: "pending", label: "Pending" },
      { value: "accepted", label: "Accepted" },
      { value: "assigned", label: "Driver assigned" },
      { value: "completed", label: "Completed" },
      { value: "cancelled", label: "Cancelled" },
    ];

    return (
      <ConsolePage
        title="Requests"
        subtitle={`Transport · ${items.length} in this view${isLoaded ? "" : " · loading…"}`}
        actions={
          <React.Fragment>
            <SearchPill
              value={search}
              onChange={(v) => this.setState({ search: v })}
              placeholder="Guest, driver or plate"
              width={250}
            />
            <Btn kind="quiet" icon="tune" onClick={() => this.props.history.push("/transport/services")}>
              Services
            </Btn>
            <Btn icon="person" onClick={() => this.props.history.push("/transport/drivers")}>
              Drivers
            </Btn>
          </React.Fragment>
        }
      >
        <style>{CSS}</style>

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <PeriodFilter value={this.state.period} onChange={this.handlePeriodChange} />
          <StatRow>
            <MiniStat label="Awaiting a decision" value={awaiting} hint="pending in this view" tone={awaiting ? "warn" : undefined} />
            <MiniStat label="In progress" value={moving} hint="accepted, assigned or under way" />
            <MiniStat
              label="Without a driver"
              value={unassigned}
              hint="open rides still unassigned"
              tone={unassigned ? "bad" : undefined}
            />
            <MiniStat label="Value in this view" value={money(value)} hint="quoted or priced rides" />
          </StatRow>

          <Panel style={{ padding: 0 }}>
            <TabPills
              tabs={tabs}
              value={activeTab}
              onChange={this.setActiveTab}
              right={
                <span style={{ fontSize: 14, color: C.taupeLight }}>
                  {rows.length} shown
                </span>
              }
            />
            <div style={{ padding: "0 10px" }}>
              {!isLoaded ? (
                <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
                  Loading requests…
                </div>
              ) : (
                <Table
                  columns={this.columns()}
                  rows={rows}
                  empty="No transport requests in this view"
                  onRowClick={(r) => this.props.history.push(`/transport/request/${r._id}`)}
                />
              )}
            </div>
          </Panel>
        </div>

        {/* Accept Confirmation Modal */}
        <ConfirmModal
          isOpen={this.state.showAcceptModal}
          toggle={this.toggleAcceptModal}
          onConfirm={this.confirmAccept}
          title="Accept Transport Request"
          message="Are you sure you want to accept this transport request?"
          confirmText="Accept"
          confirmColor="success"
          icon="ri-checkbox-circle-line"
          loading={this.state.actionLoading}
        />

        {/* Reason Input Modal (for Reject/Cancel) */}
        <Modal
          isOpen={this.state.showReasonModal}
          toggle={this.toggleReasonModal}
          centered
          className="con-modal"
        >
          <ModalHeader toggle={this.toggleReasonModal}>
            {this.state.reasonModalType === "reject" ? "Reject request" : "Cancel request"}
          </ModalHeader>
          <ModalBody>
            <div style={{ fontSize: 14, color: C.taupe, marginBottom: 14 }}>
              The guest sees this reason, so write it as you would say it to them.
            </div>
            <FormGroup>
              <Label>Reason</Label>
              <Input
                type="textarea"
                rows="3"
                value={this.state.reasonText}
                onChange={(e) => this.setState({ reasonText: e.target.value })}
                placeholder={this.state.reasonModalType === "reject"
                  ? "Enter rejection reason..."
                  : "Enter cancellation reason..."}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Btn kind="quiet" onClick={this.toggleReasonModal} disabled={this.state.actionLoading}>
              Keep it open
            </Btn>
            <Btn
              kind="danger"
              onClick={this.confirmReasonAction}
              disabled={this.state.actionLoading}
            >
              {this.state.actionLoading
                ? "Processing…"
                : this.state.reasonModalType === "reject" ? "Reject" : "Cancel request"}
            </Btn>
          </ModalFooter>
        </Modal>
      </ConsolePage>
    );
  }
}

export default TransportRequestList;
